import { browserAiToolsFilterOptionsQuerySchema, browserAiToolsFilterOptionsResponseSchema, browserAiToolsTableResponseSchema, listBrowserAiToolsDataTableParamsSchema, } from '../contracts/shadow-ai-browser-tools';
import { serializeTableQueryParams } from '../contracts/table';
import { personalToolApprovalCountsQuerySchema, toolApprovalCountsQuerySchema, toolApprovalCountsResponseSchema, toolApprovalMutationBodySchema, toolApprovalMutationResponseSchema, } from '../contracts/shadow-ai-tool-approvals';
import { personalShadowAiStatsSchema, } from '../contracts/account-shadow-ai-stats';
import { shadowAiReportRequestSchema, shadowAiToolListResponseSchema, shadowAiToolUsersResponseSchema, runningProcessListResponseSchema, shadowAiOrgStatsSchema, pendingDomainClassificationBodySchema, bulkClassifyDomainsResponseSchema, bulkClassifyDomainsBodySchema, networkConnectionsResponseSchema, networkConnectionUsersResponseSchema, networkConnectionUsersQuerySchema, networkConnectionsQuerySchema, networkConnectionsFilterOptionsQuerySchema, networkConnectionsFilterOptionsResponseSchema, networkTrafficStatsSchema, networkDomainsResponseSchema, agentNamesResponseSchema, shadowAiToolListBaseQuerySchema, shadowAiToolListQuerySchema, runningProcessListBaseQuerySchema, runningProcessListQuerySchema, bloomFiltersResponseSchema, bloomFilterChecksumResponseSchema, shadowAiSuccessResponseSchema, shadowAiDomainSchema, shadowAiToolIdSchema, shadowAiTopUsersResponseSchema, shadowAiTopToolsResponseSchema, browserAgentSitesViewResponseSchema, browserAgentSitesViewParamsSchema, browserAgentSitesFilterOptionsQuerySchema, browserAgentSitesFilterOptionsResponseSchema, browserAgentSiteUsersQuerySchema, browserAgentSiteUsersResponseSchema, setBrowserAgentDecisionRequestSchema, networkDomainHostsResponseSchema, networkDomainHostDecisionBodySchema, networkDomainHostDecisionResponseSchema, } from '../contracts/shadow-ai';
import { parseSchema } from '../errors';
import { organizationIdSchema, } from '../contracts/organizations';
import { runningNowFilterOptionsQuerySchema, runningNowFilterOptionsResponseSchema, runningNowTableResponseSchema, listRunningNowDataTableParamsSchema, } from '../contracts/shadow-ai-running-now';
import { personalRunningNowFilterOptionsQuerySchema, personalRunningNowFilterOptionsResponseSchema, personalRunningNowTableResponseSchema, listPersonalRunningNowDataTableParamsSchema, } from '../contracts/account-shadow-ai-running-now';
import { personalBrowserAiToolsFilterOptionsQuerySchema, personalBrowserAiToolsFilterOptionsResponseSchema, personalBrowserAiToolsTableResponseSchema, listPersonalBrowserAiToolsDataTableParamsSchema, } from '../contracts/account-shadow-ai-browser-tools';
import { installedToolsFilterOptionsQuerySchema, installedToolsFilterOptionsResponseSchema, installedToolsTableResponseSchema, listInstalledToolsDataTableParamsSchema, } from '../contracts/shadow-ai-installed-tools';
import { listPersonalInstalledToolsDataTableParamsSchema, personalInstalledToolsTableResponseSchema, personalInstalledToolsFilterOptionsQuerySchema, personalInstalledToolsFilterOptionsResponseSchema, } from '../contracts/account-shadow-ai-installed-tools';
function serializeParams(params) {
    const { categories, ...rest } = params;
    return {
        ...rest,
        ...(categories?.length ? { categories: categories.join(',') } : {}),
    };
}
export async function listOrganizationShadowAiTools(client, organizationId, params = {}) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedParams = parseSchema(shadowAiToolListQuerySchema, params);
    const response = await client.get(`/organizations/${validatedId}/shadow-ai/tools`, { params: serializeParams(validatedParams) });
    return parseSchema(shadowAiToolListResponseSchema, response);
}
/**
 * Browser Agent Sites view (KIR-4703 / KIR-4704): sites a browser AI agent
 * operated on, grouped per (site x agent type), with rolling-30d visits.
 */
export async function getBrowserAgentSitesView(client, organizationId, params = {}, options) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const query = parseSchema(browserAgentSitesViewParamsSchema, params);
    const response = await client.get(`/organizations/${validatedId}/shadow-ai/browser-agent-sites`, {
        params: query,
        paramsSerializer: serializeTableQueryParams,
        signal: options?.signal,
    });
    return parseSchema(browserAgentSitesViewResponseSchema, response);
}
/**
 * The users behind one Browser Agent Sites row — the Users count drill-down
 * (KIR-4704 review #15).
 */
export async function getBrowserAgentSiteUsers(client, organizationId, params) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedParams = parseSchema(browserAgentSiteUsersQuerySchema, params);
    const response = await client.get(`/organizations/${validatedId}/shadow-ai/browser-agent-sites/users`, { params: validatedParams });
    return parseSchema(browserAgentSiteUsersResponseSchema, response);
}
/**
 * Distinct values in use for a Browser Agent Sites filter column (Agent, Via),
 * for the table's multi-select filter options (KIR-4704).
 */
export async function getBrowserAgentSitesFilterOptions(client, organizationId, params) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedParams = parseSchema(browserAgentSitesFilterOptionsQuerySchema, params);
    const response = await client.get(`/organizations/${validatedId}/shadow-ai/browser-agent-sites/filter-options`, { params: validatedParams });
    return parseSchema(browserAgentSitesFilterOptionsResponseSchema, response);
}
/**
 * Set (or clear) a site's enforcement decision (KIR-4704). `action: null`
 * un-reviews the site. Owner/manager only.
 */
export async function setBrowserAgentDecision(client, organizationId, body) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedBody = parseSchema(setBrowserAgentDecisionRequestSchema, body);
    const response = await client.put(`/organizations/${validatedId}/shadow-ai/browser-agent-sites/decision`, validatedBody);
    return parseSchema(shadowAiSuccessResponseSchema, response);
}
export async function listUserShadowAiTools(client, params = {}) {
    const validatedParams = parseSchema(shadowAiToolListBaseQuerySchema, params);
    const response = await client.get('/account/shadow-ai/tools', {
        params: serializeParams(validatedParams),
    });
    return parseSchema(shadowAiToolListResponseSchema, response);
}
export async function getPersonalShadowAiStats(client) {
    const response = await client.get('/account/shadow-ai/stats');
    return parseSchema(personalShadowAiStatsSchema, response);
}
export async function getShadowAiToolUsers(client, organizationId, toolId, 
/**
 * KIR-3495: narrow the per-tool user list to one org member, so expanding a
 * row on the Shadow AI user-scope page does not reveal the whole org.
 * Honoured only for manager/owner/support (see the route).
 */
scopeUserId, 
/** KIR-5296: narrow to one table row (category + Browser / Installed). */
rowFilter) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedToolId = parseSchema(shadowAiToolIdSchema, toolId);
    const path = `/organizations/${validatedId}/shadow-ai/tools/${encodeURIComponent(validatedToolId)}/users`;
    const params = {
        ...(scopeUserId ? { scopeUserId } : {}),
        ...(rowFilter?.category ? { category: rowFilter.category } : {}),
        ...(rowFilter?.source ? { source: rowFilter.source } : {}),
    };
    // Keep the unfiltered call shape untouched — passing an explicit `undefined`
    // config would change the request arity for every existing caller.
    const response = Object.keys(params).length > 0
        ? await client.get(path, { params })
        : await client.get(path);
    return parseSchema(shadowAiToolUsersResponseSchema, response);
}
export async function submitShadowAiReport(client, report) {
    const validatedReport = parseSchema(shadowAiReportRequestSchema, report);
    const response = await client.post('/shadow-ai/report', validatedReport);
    return parseSchema(shadowAiSuccessResponseSchema, response);
}
export async function listOrganizationRunningProcesses(client, organizationId, params = {}) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedParams = parseSchema(runningProcessListQuerySchema, params);
    const response = await client.get(`/organizations/${validatedId}/shadow-ai/running`, { params: validatedParams });
    return parseSchema(runningProcessListResponseSchema, response);
}
export async function listUserRunningProcesses(client, params = {}) {
    const validatedParams = parseSchema(runningProcessListBaseQuerySchema, params);
    const response = await client.get('/account/shadow-ai/running', {
        params: validatedParams,
    });
    return parseSchema(runningProcessListResponseSchema, response);
}
export async function getOrgShadowAiStats(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/shadow-ai/stats`);
    return parseSchema(shadowAiOrgStatsSchema, response);
}
export async function listOrgNetworkConnections(client, organizationId, params = {}, options) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedParams = parseSchema(networkConnectionsQuerySchema, params);
    const response = await client.get(`/organizations/${validatedId}/shadow-ai/network-connections`, {
        params: validatedParams,
        // Encode per-column `filters` as bracket-notation query params (KIR-3496).
        paramsSerializer: serializeTableQueryParams,
        signal: options?.signal,
    });
    return parseSchema(networkConnectionsResponseSchema, response);
}
export async function getNetworkConnectionsFilterOptions(client, organizationId, params) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedParams = parseSchema(networkConnectionsFilterOptionsQuerySchema, params);
    const response = await client.get(`/organizations/${validatedId}/shadow-ai/network-connections/filter-options`, { params: validatedParams });
    return parseSchema(networkConnectionsFilterOptionsResponseSchema, response);
}
export async function getNetworkConnectionUsers(client, organizationId, domain, params = {}) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedDomain = parseSchema(shadowAiDomainSchema, domain);
    const validatedParams = parseSchema(networkConnectionUsersQuerySchema, params);
    const response = await client.get(`/organizations/${validatedId}/shadow-ai/network-connections/${encodeURIComponent(validatedDomain)}/users`, { params: validatedParams });
    return parseSchema(networkConnectionUsersResponseSchema, response);
}
/**
 * The hosts rolled up under one grouped Network Discovery row (KIR-4838).
 * `domain` may be the service apex or any host under it — the backend resolves
 * both to the same service.
 */
export async function getNetworkDomainHosts(client, organizationId, domain) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedDomain = parseSchema(shadowAiDomainSchema, domain);
    const response = await client.get(`/organizations/${validatedId}/shadow-ai/network-domains/${encodeURIComponent(validatedDomain)}/hosts`);
    return parseSchema(networkDomainHostsResponseSchema, response);
}
/**
 * Set or clear one host's decision override (KIR-4838). `decision: null` resets
 * the host to inheriting its service.
 */
export async function setNetworkDomainHostDecision(client, organizationId, domain, host, body) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedDomain = parseSchema(shadowAiDomainSchema, domain);
    const validatedHost = parseSchema(shadowAiDomainSchema, host);
    const validatedBody = parseSchema(networkDomainHostDecisionBodySchema, body);
    const response = await client.post(`/organizations/${validatedId}/shadow-ai/network-domains/${encodeURIComponent(validatedDomain)}/hosts/${encodeURIComponent(validatedHost)}/decision`, validatedBody);
    return parseSchema(networkDomainHostDecisionResponseSchema, response);
}
export async function getOrgNetworkStats(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/shadow-ai/network-stats`);
    return parseSchema(networkTrafficStatsSchema, response);
}
export async function listOrgNetworkDomains(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/shadow-ai/network/domains`);
    const parsed = parseSchema(networkDomainsResponseSchema, response);
    return parsed.domains;
}
export async function getAgentNames(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/shadow-ai/agent-names`);
    const parsed = parseSchema(agentNamesResponseSchema, response);
    return parsed.names;
}
export async function getOrgTopShadowAiUsers(client, organizationId, params = {}) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/shadow-ai/top-users`, { params });
    return parseSchema(shadowAiTopUsersResponseSchema, response);
}
export async function getOrgTopShadowAiTools(client, organizationId, params = {}) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/shadow-ai/top-active-tools`, { params });
    return parseSchema(shadowAiTopToolsResponseSchema, response);
}
export async function exportShadowAiToolsCsv(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/shadow-ai/tools/export`, { responseType: 'blob' });
    return response;
}
// Network Discovery / Pending Domains
export async function classifyDomain(client, domain, classification, organizationId, options) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedDomain = parseSchema(shadowAiDomainSchema, domain);
    const validatedBody = parseSchema(pendingDomainClassificationBodySchema, {
        classification,
        ...options,
    });
    const response = await client.post(`/organizations/${validatedId}/shadow-ai/pending-domains/${encodeURIComponent(validatedDomain)}/classify`, validatedBody);
    return parseSchema(shadowAiSuccessResponseSchema, response);
}
export async function bulkClassifyDomains(client, domains, classification, organizationId, options) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedBody = parseSchema(bulkClassifyDomainsBodySchema, {
        domains,
        classification,
        ...options,
    });
    const response = await client.post(`/organizations/${validatedId}/shadow-ai/pending-domains/bulk-classify`, validatedBody);
    return parseSchema(bulkClassifyDomainsResponseSchema, response);
}
// Organization Ignore List
export async function removeOrgIgnoreDomain(client, organizationId, domain) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedDomain = parseSchema(shadowAiDomainSchema, domain);
    const response = await client.delete(`/organizations/${validatedId}/shadow-ai/ignore-domains/${encodeURIComponent(validatedDomain)}`);
    return parseSchema(shadowAiSuccessResponseSchema, response);
}
// KIR-4854: Tool approval (org-wide, per tool)
async function mutateToolApprovals(client, organizationId, action, body) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedBody = parseSchema(toolApprovalMutationBodySchema, body);
    const response = await client.post(`/organizations/${validatedId}/shadow-ai/tool-approvals/${action}`, validatedBody);
    return parseSchema(toolApprovalMutationResponseSchema, response);
}
export async function approveShadowAiTools(client, organizationId, body) {
    return mutateToolApprovals(client, organizationId, 'approve', body);
}
export async function unapproveShadowAiTools(client, organizationId, body) {
    return mutateToolApprovals(client, organizationId, 'unapprove', body);
}
export async function getShadowAiToolApprovalCounts(client, organizationId, query, options) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedQuery = parseSchema(toolApprovalCountsQuerySchema, query);
    const response = await client.get(`/organizations/${validatedId}/shadow-ai/tool-approvals/counts`, { params: validatedQuery, signal: options?.signal });
    return parseSchema(toolApprovalCountsResponseSchema, response);
}
// KIR-4864: Tool approval on the caller's personal account (never an org's)
async function mutatePersonalToolApprovals(client, action, body) {
    const validatedBody = parseSchema(toolApprovalMutationBodySchema, body);
    const response = await client.post(`/account/shadow-ai/tool-approvals/${action}`, validatedBody);
    return parseSchema(toolApprovalMutationResponseSchema, response);
}
export async function approvePersonalShadowAiTools(client, body) {
    return mutatePersonalToolApprovals(client, 'approve', body);
}
export async function unapprovePersonalShadowAiTools(client, body) {
    return mutatePersonalToolApprovals(client, 'unapprove', body);
}
export async function getPersonalShadowAiToolApprovalCounts(client, query, options) {
    const validatedQuery = parseSchema(personalToolApprovalCountsQuerySchema, query);
    const response = await client.get('/account/shadow-ai/tool-approvals/counts', {
        params: validatedQuery,
        signal: options?.signal,
    });
    return parseSchema(toolApprovalCountsResponseSchema, response);
}
// Classified Domains (Sanctioned AI list)
export async function removeClassifiedDomain(client, domain, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedDomain = parseSchema(shadowAiDomainSchema, domain);
    const response = await client.delete(`/organizations/${validatedId}/shadow-ai/classified-domains/${encodeURIComponent(validatedDomain)}`);
    return parseSchema(shadowAiSuccessResponseSchema, response);
}
/**
 * Get bloom filters for Shadow AI detection
 */
export async function getBloomFilters(client) {
    const response = await client.get('/shadow-ai/bloom-filters');
    return parseSchema(bloomFiltersResponseSchema, response);
}
/**
 * Get bloom filter checksum/version
 */
export async function getBloomFilterChecksum(client) {
    const response = await client.get('/shadow-ai/bloom-filters/checksum');
    return parseSchema(bloomFilterChecksumResponseSchema, response);
}
export async function listBrowserAiToolsDataTable(client, organizationId, params = {}, options) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedParams = parseSchema(listBrowserAiToolsDataTableParamsSchema, params);
    const response = await client.get(`/organizations/${validatedId}/browser-ai-tools`, {
        params: validatedParams,
        paramsSerializer: serializeTableQueryParams,
        signal: options?.signal,
    });
    return parseSchema(browserAiToolsTableResponseSchema, response);
}
export async function getBrowserAiToolsFilterOptions(client, organizationId, params) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedParams = parseSchema(browserAiToolsFilterOptionsQuerySchema, params);
    const response = await client.get(`/organizations/${validatedId}/browser-ai-tools/filter-options`, { params: validatedParams });
    return parseSchema(browserAiToolsFilterOptionsResponseSchema, response);
}
export async function listRunningNowDataTable(client, organizationId, params = {}, options) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedParams = parseSchema(listRunningNowDataTableParamsSchema, params);
    const response = await client.get(`/organizations/${validatedId}/running-now`, {
        params: validatedParams,
        paramsSerializer: serializeTableQueryParams,
        signal: options?.signal,
    });
    return parseSchema(runningNowTableResponseSchema, response);
}
export async function getRunningNowFilterOptions(client, organizationId, params) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedParams = parseSchema(runningNowFilterOptionsQuerySchema, params);
    const response = await client.get(`/organizations/${validatedId}/running-now/filter-options`, { params: validatedParams });
    return parseSchema(runningNowFilterOptionsResponseSchema, response);
}
/**
 * List the signed-in user's running AI processes (personal, org-agnostic).
 * Response wrapper additionally carries `lastHeartbeat` for AgentStatus.
 */
export async function listPersonalRunningNowDataTable(client, params = {}, options) {
    const validatedParams = parseSchema(listPersonalRunningNowDataTableParamsSchema, params);
    const response = await client.get('/account/shadow-ai/running-now', {
        params: validatedParams,
        paramsSerializer: serializeTableQueryParams,
        signal: options?.signal,
    });
    return parseSchema(personalRunningNowTableResponseSchema, response);
}
/**
 * Distinct filter option values for a personal running-now field
 * (name | category | processUser), scoped to the signed-in user.
 */
export async function getPersonalRunningNowFilterOptions(client, params) {
    const validatedParams = parseSchema(personalRunningNowFilterOptionsQuerySchema, params);
    const response = await client.get('/account/shadow-ai/running-now/filter-options', { params: validatedParams });
    return parseSchema(personalRunningNowFilterOptionsResponseSchema, response);
}
/**
 * List the signed-in user's browser AI tools (personal, org-agnostic).
 * Columns mirror org browser-tools minus the userCount column.
 */
export async function listPersonalBrowserAiToolsDataTable(client, params = {}, options) {
    const validatedParams = parseSchema(listPersonalBrowserAiToolsDataTableParamsSchema, params);
    const response = await client.get('/account/shadow-ai/browser-ai-tools', {
        params: validatedParams,
        paramsSerializer: serializeTableQueryParams,
        signal: options?.signal,
    });
    return parseSchema(personalBrowserAiToolsTableResponseSchema, response);
}
/**
 * Distinct filter option values for a personal browser-ai-tools field
 * (domain | name | category), scoped to the signed-in user.
 */
export async function getPersonalBrowserAiToolsFilterOptions(client, params) {
    const validatedParams = parseSchema(personalBrowserAiToolsFilterOptionsQuerySchema, params);
    const response = await client.get('/account/shadow-ai/browser-ai-tools/filter-options', { params: validatedParams });
    return parseSchema(personalBrowserAiToolsFilterOptionsResponseSchema, response);
}
export async function listInstalledAiToolsDataTable(client, organizationId, params = {}, options) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedParams = parseSchema(listInstalledToolsDataTableParamsSchema, params);
    const response = await client.get(`/organizations/${validatedId}/installed-ai-tools`, {
        params: validatedParams,
        paramsSerializer: serializeTableQueryParams,
        signal: options?.signal,
    });
    return parseSchema(installedToolsTableResponseSchema, response);
}
export async function getInstalledAiToolsFilterOptions(client, organizationId, params) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedParams = parseSchema(installedToolsFilterOptionsQuerySchema, params);
    const response = await client.get(`/organizations/${validatedId}/installed-ai-tools/filter-options`, { params: validatedParams });
    return parseSchema(installedToolsFilterOptionsResponseSchema, response);
}
/**
 * List the signed-in user's installed AI tools (personal, org-agnostic). The
 * row drops the org-only `userCount`/`users` column and the wrapper carries no
 * `lastHeartbeat`.
 */
export async function listPersonalInstalledToolsDataTable(client, params = {}, options) {
    const validatedParams = parseSchema(listPersonalInstalledToolsDataTableParamsSchema, params);
    const response = await client.get('/account/shadow-ai/installed-tools', {
        params: validatedParams,
        paramsSerializer: serializeTableQueryParams,
        signal: options?.signal,
    });
    return parseSchema(personalInstalledToolsTableResponseSchema, response);
}
/**
 * Distinct filter option values for a personal installed-tools field
 * (name | category), scoped to the signed-in user.
 */
export async function getPersonalInstalledToolsFilterOptions(client, params) {
    const validatedParams = parseSchema(personalInstalledToolsFilterOptionsQuerySchema, params);
    const response = await client.get('/account/shadow-ai/installed-tools/filter-options', { params: validatedParams });
    return parseSchema(personalInstalledToolsFilterOptionsResponseSchema, response);
}
//# sourceMappingURL=shadow-ai.js.map